/**
 * CitiesRegionsExplorer — 3-column drill-down on the Routes & Regions tab.
 *
 * COLUMN 1 · Regions
 *   - All DFW business regions (R01–R15) with property counts.
 *   - Selecting a region reveals its cities in Column 2.
 *
 * COLUMN 2 · Cities
 *   - Numbered list of cities under the selected region.
 *   - Each city badges the # of properties already on file.
 *   - Selecting a city reveals its properties in Column 3.
 *   - Ops-only "+ Add Property" quick action per city.
 *
 * COLUMN 3 · Properties
 *   - Full property cards for the selected city:
 *     name, address, buildings, units, PM/contract status.
 *   - Powers Managers & Supervisors making crew leader / agent
 *     assignments through their crew-leader portals.
 */
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  MapPin, Building2, Plus, Hash, Users, ChevronRight,
  Home, FileCheck, UserCog,
} from 'lucide-react';
import { useMemo, useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CitiesRegionsExplorer({
  regions = [],
  properties = [],
  onPropertyCreated,
  readOnly = false, // supervisors/managers pass true — hides "Add Property"
  bigCity = null, // optional Big City filter (Dallas, Houston, Austin, San Antonio, Tyler)
}) {
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [addDialog, setAddDialog] = useState({ open: false, city: '', region: null });
  const [form, setForm] = useState({
    property_name: '', address: '', num_buildings: 1, num_units: 0, zip_code: '',
  });
  const [saving, setSaving] = useState(false);

  // ── Aggregations ──────────────────────────────────────────────────────────
  // Count properties per city (lowercase key) and per region (region_id).
  const { propsByCity, propsByRegion, propsByRegionCity } = useMemo(() => {
    const city = {};
    const region = {};
    const regionCity = {}; // region_id → { cityLower → [properties] }
    (properties || []).forEach((p) => {
      const cKey = (p.city || '').trim().toLowerCase();
      if (cKey) city[cKey] = (city[cKey] || 0) + 1;
      if (p.region_id) {
        region[p.region_id] = (region[p.region_id] || 0) + 1;
        regionCity[p.region_id] = regionCity[p.region_id] || {};
        if (cKey) {
          regionCity[p.region_id][cKey] = regionCity[p.region_id][cKey] || [];
          regionCity[p.region_id][cKey].push(p);
        }
      }
    });
    return { propsByCity: city, propsByRegion: region, propsByRegionCity: regionCity };
  }, [properties]);

  // Regions filtered by Big City if provided — otherwise the full list.
  // Sorted by region_code so R01 always appears first.
  const usableRegions = useMemo(() => {
    return (regions || [])
      .filter((r) => (r.cities || []).length > 0)
      .filter((r) => !bigCity || (r.big_city || 'Unassigned') === bigCity)
      .slice()
      .sort((a, b) => (a.region_code || 'zzz').localeCompare(b.region_code || 'zzz'));
  }, [regions, bigCity]);
  const selectedRegion = useMemo(
    () => usableRegions.find((r) => r.region_id === selectedRegionId) || null,
    [usableRegions, selectedRegionId],
  );

  // When Big City changes, reset the region selection so the first
  // region of the new metro is auto-highlighted.
  useEffect(() => {
    setSelectedRegionId(null);
    setSelectedCity(null);
  }, [bigCity]);

  // Auto-select first region on initial load so ops instantly sees data
  useEffect(() => {
    if (!selectedRegionId && usableRegions.length > 0) {
      setSelectedRegionId(usableRegions[0].region_id);
    }
  }, [usableRegions, selectedRegionId]);

  // When region changes, reset city selection to the first city with props
  useEffect(() => {
    if (!selectedRegion) { setSelectedCity(null); return; }
    const citiesList = selectedRegion.cities || [];
    if (citiesList.length === 0) { setSelectedCity(null); return; }
    // Prefer the first city that already has properties on file
    const cityWithProps = citiesList.find(
      (c) => (propsByCity[c.trim().toLowerCase()] || 0) > 0,
    );
    setSelectedCity(cityWithProps || citiesList[0]);
  }, [selectedRegion, propsByCity]);

  const propertiesForSelectedCity = useMemo(() => {
    if (!selectedRegion || !selectedCity) return [];
    const cKey = selectedCity.trim().toLowerCase();
    const bucket = (propsByRegionCity[selectedRegion.region_id] || {})[cKey] || [];
    // Also fold in properties matched by city name but region unset (safety net)
    const unregioned = (properties || []).filter((p) =>
      (p.city || '').trim().toLowerCase() === cKey && !p.region_id,
    );
    return [...bucket, ...unregioned];
  }, [selectedRegion, selectedCity, propsByRegionCity, properties]);

  // ── Add property flow ─────────────────────────────────────────────────────
  const openAdd = (city, region) => {
    setAddDialog({ open: true, city, region });
    setForm({ property_name: '', address: '', num_buildings: 1, num_units: 0, zip_code: '' });
  };

  const save = async () => {
    if (!form.property_name || !form.address) {
      return toast.error('Property name + address are required');
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/properties`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_name: form.property_name.trim(),
          address: form.address.trim(),
          city: addDialog.city,
          state: 'TX',
          region_code: addDialog.region?.region_code || null,
          num_buildings: Number(form.num_buildings) || 1,
          num_units: Number(form.num_units) || 0,
          zip_code: form.zip_code || null,
        }),
      });
      if (res.ok) {
        toast.success(`${form.property_name} registered under ${addDialog.city}`);
        setAddDialog({ open: false, city: '', region: null });
        if (onPropertyCreated) onPropertyCreated();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Failed to register property');
      }
    } catch (_) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const totalProps = properties.length;
  const totalCities = usableRegions.reduce((s, r) => s + (r.cities || []).length, 0);

  return (
    <>
      <Card
        className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-5"
        data-testid="cities-regions-explorer"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h3
              className="text-lg font-bold text-white flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <MapPin className="h-5 w-5 text-[#FFD60A]" />
              {bigCity ? `${bigCity.toUpperCase()} · CITIES & REGIONS · EXPLORER` : 'CITIES & REGIONS · EXPLORER'}
            </h3>
            <p className="text-xs text-zinc-400 mt-1 max-w-3xl">
              {bigCity ? (
                <>
                  Showing only the <strong className="text-[#C9A962]">{bigCity}</strong> metro. Drill down <strong className="text-[#C9A962]">Region → City → Property</strong>. Regions and cities show
                  live property counts; the properties column feeds Managers &amp; Supervisors when they assign
                  Crew Leaders and Agents through their portals.
                </>
              ) : (
                <>
                  Drill down <strong className="text-[#C9A962]">Region → City → Property</strong>. Regions and cities show
                  live property counts; the properties column feeds Managers &amp; Supervisors when they assign
                  Crew Leaders and Agents through their portals.
                </>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className="bg-[#C9A962] text-black font-bold" data-testid="explorer-region-total">
              {usableRegions.length} regions
            </Badge>
            <Badge className="bg-zinc-700 text-white" data-testid="explorer-city-total">
              {totalCities} cities
            </Badge>
            <Badge className="bg-emerald-700 text-white" data-testid="explorer-prop-total">
              <Building2 className="h-3 w-3 mr-1" /> {totalProps} properties
            </Badge>
          </div>
        </div>

        {/* 3-column drill-down */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* ── Column 1 · Regions ─────────────────────────────────────── */}
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-widest text-[#C9A962] font-bold mb-2 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Regions
            </div>
            <ul
              className="space-y-1 max-h-[520px] overflow-y-auto pr-1"
              data-testid="explorer-region-list"
            >
              {usableRegions.map((r) => {
                const active = r.region_id === selectedRegionId;
                const count = propsByRegion[r.region_id] || 0;
                return (
                  <li key={r.region_id}>
                    <button
                      type="button"
                      onClick={() => setSelectedRegionId(r.region_id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left transition-colors border ${
                        active
                          ? 'bg-[#FFD60A]/10 border-[#FFD60A]'
                          : 'bg-zinc-950/40 border-zinc-800 hover:bg-zinc-900'
                      }`}
                      data-testid={`explorer-region-btn-${r.region_code || r.region_id}`}
                    >
                      <span
                        className="h-3 w-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: r.color || '#C9A962' }}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-white text-sm font-semibold truncate">
                          <span className="font-mono text-[#C9A962] mr-1">{r.region_code || '—'}</span>
                          {r.name}
                        </span>
                        <span className="block text-[10px] text-zinc-500">
                          {(r.cities || []).length} cities
                        </span>
                      </span>
                      <Badge
                        className={`text-[10px] font-bold ${
                          count > 0 ? 'bg-emerald-700 text-white' : 'bg-zinc-800 text-zinc-400'
                        }`}
                        data-testid={`explorer-region-prop-count-${r.region_code || r.region_id}`}
                      >
                        {count}
                      </Badge>
                      {active && <ChevronRight className="h-4 w-4 text-[#FFD60A]" />}
                    </button>
                  </li>
                );
              })}
              {usableRegions.length === 0 && (
                <li
                  className="text-xs text-zinc-500 text-center py-6"
                  data-testid="cities-regions-empty"
                >
                  {bigCity
                    ? `No regions in ${bigCity} yet — seed the ${bigCity} catalog to enable this metro.`
                    : 'No regions with cities on file yet — seed the DFW catalog first.'}
                </li>
              )}
            </ul>
          </div>

          {/* ── Column 2 · Cities in selected region ───────────────────── */}
          <div className="lg:col-span-4">
            <div className="text-[11px] uppercase tracking-widest text-[#C9A962] font-bold mb-2 flex items-center gap-1">
              <Home className="h-3 w-3" />
              {selectedRegion
                ? `Cities · ${selectedRegion.region_code || ''} ${selectedRegion.name}`
                : 'Cities'}
            </div>
            <ul
              className="space-y-1 max-h-[520px] overflow-y-auto pr-1"
              data-testid="explorer-city-list"
            >
              {selectedRegion && (selectedRegion.cities || []).map((city, idx) => {
                const cKey = (city || '').trim().toLowerCase();
                const count = propsByCity[cKey] || 0;
                const active = selectedCity === city;
                return (
                  <li key={city}>
                    <div
                      className={`flex items-center gap-2 rounded-md border ${
                        active
                          ? 'bg-[#FFD60A]/10 border-[#FFD60A]'
                          : 'bg-zinc-950/40 border-zinc-800 hover:bg-zinc-900'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedCity(city)}
                        className="flex-1 flex items-center gap-2 px-3 py-2 text-left"
                        data-testid={`explorer-city-btn-${selectedRegion.region_code}-${idx + 1}`}
                      >
                        <span className="text-[11px] font-mono text-[#C9A962] font-bold min-w-[28px]">
                          <Hash className="h-3 w-3 inline-block mr-0.5" />{idx + 1}
                        </span>
                        <span className="text-white text-sm flex-1 truncate">{city}</span>
                        <Badge
                          className={`text-[10px] font-bold ${
                            count > 0 ? 'bg-emerald-700 text-white' : 'bg-zinc-800 text-zinc-400'
                          }`}
                          data-testid={`explorer-city-prop-count-${selectedRegion.region_code}-${idx + 1}`}
                        >
                          <Building2 className="h-3 w-3 mr-1" />
                          {count}
                        </Badge>
                      </button>
                      {!readOnly && (
                        <Button
                          size="sm"
                          onClick={() => openAdd(city, selectedRegion)}
                          className="mr-1 h-7 px-2 bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                          data-testid={`explorer-add-property-${selectedRegion.region_code}-${idx + 1}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
              {!selectedRegion && (
                <li className="text-xs text-zinc-500 text-center py-6">
                  ← Select a region to see its cities.
                </li>
              )}
            </ul>
          </div>

          {/* ── Column 3 · Properties in selected city ─────────────────── */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-widest text-[#C9A962] font-bold mb-2 flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {selectedCity
                ? `Properties · ${selectedCity} (${propertiesForSelectedCity.length})`
                : 'Properties'}
            </div>
            <ul
              className="space-y-2 max-h-[520px] overflow-y-auto pr-1"
              data-testid="explorer-property-list"
            >
              {propertiesForSelectedCity.length === 0 && selectedCity && (
                <li
                  className="text-xs text-zinc-500 text-center py-6 border border-dashed border-zinc-700 rounded-md"
                  data-testid="explorer-property-empty"
                >
                  No properties in {selectedCity} yet.
                  {!readOnly && (
                    <>
                      {' '}Use the <Plus className="h-3 w-3 inline" /> button in the city list to add one.
                    </>
                  )}
                </li>
              )}
              {propertiesForSelectedCity.map((p) => (
                <PropertyDetailCard
                  key={p.property_id}
                  property={p}
                  region={selectedRegion}
                  readOnly={readOnly}
                />
              ))}
              {!selectedCity && (
                <li className="text-xs text-zinc-500 text-center py-6">
                  ← Select a city to see its properties.
                </li>
              )}
            </ul>
          </div>
        </div>
      </Card>

      {/* Add-property dialog (Ops-only) */}
      <Dialog
        open={addDialog.open}
        onOpenChange={(v) => !v && setAddDialog({ open: false, city: '', region: null })}
      >
        <DialogContent className="bg-white text-[#2D2D2D] border border-[#C9A962]/40 sm:max-w-md max-h-[80vh] overflow-y-auto p-4">
          <DialogHeader>
            <DialogTitle className="text-[#2D2D2D] flex items-center gap-2">
              <Building2 className="h-5 w-5" /> Register Property
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="bg-[#FAF7EF] border border-[#C9A962]/30 rounded-md p-2.5 text-sm">
              <div className="text-xs text-zinc-600 uppercase tracking-widest">Under</div>
              <div className="font-bold text-[#2D2D2D]">{addDialog.city}, TX</div>
              <div className="text-xs text-[#C9A962] font-semibold mt-0.5">
                {addDialog.region?.region_code} · {addDialog.region?.name}
              </div>
            </div>
            <div>
              <Label className="text-[#2D2D2D] font-semibold text-sm">Property name *</Label>
              <Input
                value={form.property_name}
                onChange={(e) => setForm((f) => ({ ...f, property_name: e.target.value }))}
                placeholder="e.g., Sunset Vista Apartments"
                className="bg-white text-[#2D2D2D] border-zinc-300 mt-1"
                data-testid="cities-add-name"
              />
            </div>
            <div>
              <Label className="text-[#2D2D2D] font-semibold text-sm">Address *</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="Street address"
                className="bg-white text-[#2D2D2D] border-zinc-300 mt-1"
                data-testid="cities-add-address"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[#2D2D2D] font-semibold text-sm">Buildings</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.num_buildings}
                  onChange={(e) => setForm((f) => ({ ...f, num_buildings: e.target.value }))}
                  className="bg-white text-[#2D2D2D] border-zinc-300 mt-1"
                  data-testid="cities-add-buildings"
                />
              </div>
              <div>
                <Label className="text-[#2D2D2D] font-semibold text-sm">Units</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.num_units}
                  onChange={(e) => setForm((f) => ({ ...f, num_units: e.target.value }))}
                  className="bg-white text-[#2D2D2D] border-zinc-300 mt-1"
                  data-testid="cities-add-units"
                />
              </div>
              <div>
                <Label className="text-[#2D2D2D] font-semibold text-sm">Zip</Label>
                <Input
                  value={form.zip_code}
                  onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))}
                  placeholder="Optional"
                  className="bg-white text-[#2D2D2D] border-zinc-300 mt-1"
                  data-testid="cities-add-zip"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddDialog({ open: false, city: '', region: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !form.property_name || !form.address}
              className="bg-[#FFD60A] text-black font-bold hover:bg-[#EAB308]"
              data-testid="cities-add-save"
            >
              {saving ? 'Saving…' : 'Register + Show on Map'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Compact property card used in Column 3 of the drill-down. Shows all
 *  operational info a Manager or Supervisor needs to assign a Crew
 *  Leader and Agents from their portal. */
function PropertyDetailCard({ property: p, region, readOnly }) {
  const regionColor = region?.color || '#C9A962';
  const hasPM = Boolean(p.manager_id);
  return (
    <li
      className="bg-zinc-950 border border-zinc-800 rounded-lg p-3"
      data-testid={`explorer-property-card-${p.property_id}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-white truncate">{p.property_name}</div>
          {/* Master Addendum · Auto-generated NECS property/facility ID
              displayed inline right beneath the property name. */}
          {(p.necs_code || p.property_code) && (
            <div
              className="text-[10px] font-mono uppercase tracking-widest mt-0.5"
              style={{ color: '#C9A962' }}
              data-testid={`explorer-property-code-${p.property_id}`}
            >
              {p.necs_code || p.property_code}
            </div>
          )}
          <div className="text-[11px] text-zinc-400 truncate flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{p.address || '—'}</span>
          </div>
        </div>
        <Badge
          className="text-[10px] font-bold text-black"
          style={{ backgroundColor: regionColor }}
        >
          {p.region_code || region?.region_code || '—'}
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
        <StatCell label="Buildings" value={p.num_buildings || p.total_buildings || 1} />
        <StatCell label="Units" value={p.num_units || p.total_units || 0} />
        <StatCell label="Package" value={p.package_type || 'Standard'} small />
      </div>

      <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-[10px]">
          {hasPM ? (
            <Badge className="bg-emerald-700 text-white">
              <UserCog className="h-3 w-3 mr-1" />
              PM: {p.manager_name || 'Assigned'}
            </Badge>
          ) : (
            <Badge className="bg-amber-700 text-white">
              <UserCog className="h-3 w-3 mr-1" /> No PM
            </Badge>
          )}
          {p.latitude && (
            <Badge className="bg-zinc-700 text-white">
              <FileCheck className="h-3 w-3 mr-1" /> Geocoded
            </Badge>
          )}
        </div>
        {!readOnly && (
          <span className="text-[10px] text-zinc-500 flex items-center gap-1">
            <Users className="h-3 w-3" />
            Use Job Routes to assign a crew leader
          </span>
        )}
      </div>
    </li>
  );
}

function StatCell({ label, value, small = false }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded p-1.5">
      <div className={`font-bold text-white ${small ? 'text-[11px]' : 'text-sm'}`}>{value}</div>
      <div className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}
